import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../api/client';
import { StarRating } from '../components/StarRating';
import { SkeletonBar, Button } from '../components/ui';
import { T } from '../styles/tokens';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; border: string }> = {
  queued:    { label: 'Na fila',     color: T.textSecondary, bg: T.bgHover, border: T.borderHover },
  running:   { label: 'Executando',  color: T.primary, bg: T.primaryBg,   border: T.primaryBg },
  leased:    { label: 'Executando',  color: T.primary, bg: T.primaryBg,   border: T.primaryBg },
  done:      { label: 'Concluído',   color: T.success, bg: T.successBg,   border: T.successBg },
  failed:    { label: 'Falhou',      color: T.danger, bg: T.dangerBg,    border: T.dangerBg },
  cancelled: { label: 'Cancelado',   color: T.textSecondary, bg: T.bgHover, border: T.borderHover },
};

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min atrás`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atrás`;
  return `${Math.floor(hours / 24)}d atrás`;
}

export function RunHistoryPage({ path }: { path?: string }) {
  const [runs, setRuns] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchRuns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get<any>(`/runs?${params}`);
      setRuns(res?.runs ?? []);
      setTotal(res?.pagination?.total ?? 0);
    } catch (e) { console.error('Failed to fetch runs:', e); }
    setLoading(false);
  };

  useEffect(() => { fetchRuns(); }, [page, statusFilter]);
  useEffect(() => {
    const hasRunning = runs.some(r => r.status === 'running' || r.status === 'leased');
    if (!hasRunning) return;
    const interval = setInterval(fetchRuns, 10000);
    return () => clearInterval(interval);
  }, [runs]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Aurora */}
      <div style={{
        position: 'absolute', top: -120, left: '50%', transform: 'translateX(-50%)',
        width: 500, height: 300, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(ellipse, ${T.primaryBgSubtle} 0%, ${T.accentBgSubtle} 40%, transparent 70%)`,
      }} />

      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>Tarefas</h1>
          <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
            {total > 0 ? `${total} execuções no total` : 'Acompanhe suas execuções'}
          </p>
        </div>
        <Button variant="ghost" onClick={() => route('/create')} class="btn btn-primary" style={{ fontSize: 13 }}>
 Criar tarefa
        </Button>
      </div>

      {/* Filters */}
      {(total > 0 || statusFilter) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          {['', 'running', 'done', 'failed', 'cancelled'].map(s => {
            const active = statusFilter === s;
            const info = s ? STATUS_MAP[s] : null;
            const label = s ? info!.label : 'Todos';
            return (
              <Button variant="ghost" key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{
                padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                border: active ? `1px solid ${T.primary}` : `1px solid ${T.border}`,
                background: active ? T.primaryBg : 'transparent',
                color: active ? T.primary : T.textMuted,
                cursor: 'pointer', transition: 'all 0.15s',
              }}>
                {label}
              </Button>
            );
          })}
        </div>
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {loading && runs.length === 0 ? (
          <div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.borderSubtle}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>
        ) : runs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: T.bgCard, border: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6 }}>Nenhuma execução ainda</h3>
            <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 20, lineHeight: 1.5 }}>
              Vá até uma ação e clique em Executar para começar.
            </p>
            <Button variant="ghost" onClick={() => route('/')} class="btn btn-primary" style={{ fontSize: 13 }}>
              Ir para o Dashboard
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {runs.map((run: any) => {
              const s = STATUS_MAP[run.status] || STATUS_MAP.queued;
              const done = run.progress_done || 0;
              const failed = run.progress_failed || 0;
              const runTotal = run.progress_total || 1;
              const progress = Math.round(((done + failed) / runTotal) * 100);
              const isRunning = run.status === 'running' || run.status === 'leased';

              return (
                <div key={run.id} onClick={() => route(`/runs/${run.id}`)} class="v2-task-row" style={{
                  background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14,
                  padding: '14px 18px', cursor: 'pointer', transition: 'all 0.2s',
                  position: 'relative', overflow: 'hidden',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {run.action_name || 'Ação'}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{timeAgo(run.created_at)}</div>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: T.radiusFull,
                      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
                      flexShrink: 0, marginLeft: 12,
                    }}>{s.label}</span>
                  </div>

                  {/* Progress bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 4, borderRadius: 2, background: T.bgCard, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 2, width: `${progress}%`,
                        background: run.status === 'failed' ? T.danger
                          : run.status === 'done' ? T.success
                          : `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
                        transition: 'width 0.5s ease',
                        ...(isRunning ? { animation: 'pulse-bar 2s ease-in-out infinite' } : {}),
                      }} />
                    </div>
                    <span style={{ fontSize: 11, color: T.textMuted, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                      {done}/{runTotal}
                    </span>
                  </div>

                  {/* Rating (only for terminal tasks) */}
                  {(run.status === 'done' || run.status === 'failed') && (
                    <div style={{ marginTop: 8 }} onClick={(e: Event) => e.stopPropagation()}>
                      <StarRating taskId={run.id} size={16} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 24 }}>
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
            <span style={{ fontSize: 12, color: T.textMuted }}>Página {page}</span>
            <Button variant="ghost" size="sm" disabled={runs.length < 20} onClick={() => setPage(page + 1)}>Próxima</Button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse-bar { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
        .v2-task-row:hover { border-color: ${T.primaryBgStrong} !important; box-shadow: 0 0 20px ${T.primaryBgSubtle}; }
      `}</style>
    </div>
  );
}
